/**
 * dispatchReadiness — the SINGLE rule deciding whether an order may be handed to a courier.
 * Used by the Pre-Shipment Desk table AND the assign handler so the two can never drift.
 *
 *   COD      → an agent must have call-verified it (codConfirmation.status === 'confirmed').
 *   Prepaid  → the payment must actually be VERIFIED. Cashfree reconciliation sets
 *              payment.verificationStatus = 'PAID' (or an agent verifies it manually).
 *              IMPORTANT: payment.mode === 'Prepaid' is NOT proof of payment — a website
 *              order can be created as "Prepaid" while the payment is still pending,
 *              failed or abandoned. Those must never ship.
 */

import { amountIntegrity } from './amountIntegrity';
import { isPartialCod, canShipCod, codAdvance } from './codAmount';
import { discountApprovalPending } from './discountPolicy';

/** Owner, 29 Oct 2026: the minimum COD advance every COD order must carry. */
export const MIN_COD_ADVANCE = 99;
/** Owner, 30 Oct 2026: a manager may let a COD order ship WITHOUT the advance (manager-only field). */
export const codFeeApproved = (order) => !!order?.dispatchApproval?.codFee?.approvedBy;

export const paymentVerification = (order) => String(order?.payment?.verificationStatus || '').toUpperCase();
export const isPaidVerified = (order) => paymentVerification(order) === 'PAID';
export const isCODOrder = (order) => (order?.payment?.mode || '') === 'COD';
export const isCODConfirmed = (order) => order?.codConfirmation?.status === 'confirmed';

/**
 * PAYMENT MODE LOCK: once the money is verified as received, the payment mode is a
 * FACT, not a setting. Switching a paid order to COD / Not Confirmed would make the
 * courier collect cash a second time and corrupt the audit trail, so it is refused.
 * (Re-opening it is a correction, not an edit — clear the verification first.)
 */
export const isPaymentLocked = (order) => isPaidVerified(order);

/**
 * GATEWAY PROOF for website orders. dakshaorganics.com writes its orders straight into
 * the database, so "PAID" on a website order is only believable with the gateway's own
 * payment id (written by the ERP's Cashfree check, or the signed anjenna.com bridge for
 * Razorpay) or a staff member's manual verification. Forward-only: orders created
 * before PROOF_FROM (27 Oct 2026) keep the old rule — 540 legacy paid orders carry no id.
 */
export const PROOF_FROM = Date.parse('2026-10-27T00:00:00+05:30');
const WEBSITE_SOURCES = ['website', 'anjenna_website'];
const createdMs = (order) => {
  const v = order?.createdAt;
  return (typeof v === 'number' ? v : Date.parse(v || '')) || 0;
};
export const hasGatewayProof = (order) => {
  const p = order?.payment || {};
  return !!(p.paymentAudit?.cf_payment_id || p.cashfree_payment_id || p.cf_payment_id
    || p.razorpay_payment_id || String(p.sourceOfTruth || '').toLowerCase() === 'manual');
};
export const needsGatewayProof = (order) => WEBSITE_SOURCES.includes(String(order?.source || ''))
  && createdMs(order) >= PROOF_FROM && !hasGatewayProof(order);

/**
 * "Prepaid Ready" means THE MONEY IS IN — verified PAID (gateway or manual) and
 * the order still in play. Mode alone is a customer's intention, not a payment:
 * an unpaid "Prepaid" order at Payment Pending was being counted ready to ship.
 */
/** Prepaid INTENDED but the money is NOT confirmed — the follow-up pile. */
export const isPrepaidUnverified = (order) => {
    const st = String(order?.status || '');
    if (['Cancelled', 'Delivered', 'Shipped'].includes(st)) return false;
    return !isCODOrder(order) && !isPaidVerified(order);
};

export const isPrepaidReady = (order) => {
    const st = String(order?.status || '');
    if (['Cancelled', 'Delivered', 'Shipped', 'Payment Pending', 'Payment Failed'].includes(st)) return false;
    return !isCODOrder(order) && isPaidVerified(order);
};
export const paymentLockReason = (order) => {
  const src = String(order?.payment?.sourceOfTruth || '').toLowerCase();
  const a = order?.payment?.paymentAudit || {};
  const ref = a.bank_reference || a.upi_id || order?.payment?.cashfree_payment_id || order?.payment?.manualReference;
  return `Payment already verified as RECEIVED${src === 'cashfree' ? ' by Cashfree' : src === 'manual' ? ' manually' : ''}${ref ? ` (ref ${ref})` : ''}.\n\nThe payment mode of a paid order cannot be changed to COD or Not Confirmed — the courier would collect the money twice and the audit trail would no longer match the bank statement.`;
};

/** Human label for the payment-verification state (prepaid orders). */
export const paymentStateLabel = (order) => {
  const vs = paymentVerification(order);
  if (vs === 'PAID') return { text: 'PAID', tone: 'ok' };
  if (vs === 'FAILED') return { text: 'PAYMENT FAILED', tone: 'bad' };
  if (vs === 'ABANDONED') return { text: 'ABANDONED', tone: 'bad' };
  return { text: 'NOT VERIFIED', tone: 'warn' };
};

/**
 * @returns {{ok:boolean, kind?:'cod'|'payment', label?:string, hint?:string, blockMessage?:string}}
 */
/** opts.codFeeRule=false judges by the rules before 29 Oct 2026 (no ₹99 advance needed). */
export const dispatchReadiness = (order, opts = {}) => {
  // A discount above the limit waits for a manager, whatever the payment mode.
  if (discountApprovalPending(order)) {
    const d = order.discountApproval || {};
    return {
      ok: false,
      kind: 'discount',
      label: `Manager approval · ₹${d.amount || 0} off`,
      hint: `Discount above ₹${d.limit || 100} by ${d.requestedBy || 'staff'} — a manager must approve`,
      blockMessage: `This order has ₹${d.amount || 0} off (asked by ${d.requestedBy || 'staff'}), above the ₹${d.limit || 100} limit.

A manager must approve the discount before it can go to a courier.`,
    };
  }
  if (isCODOrder(order)) {
    // Owner, 29 Oct 2026: every COD order carries at least the Rs 99 advance, paid
    // online, or it does not go to a courier (a manager may override, logged).
    if (opts.codFeeRule !== false && !codFeeApproved(order) && codAdvance(order) < MIN_COD_ADVANCE) return {
        ok: false,
        kind: 'cod_fee',
        label: '₹' + MIN_COD_ADVANCE + ' COD advance missing',
        hint: 'COD orders need the ₹' + MIN_COD_ADVANCE + ' advance — add it (Partial COD) and collect it',
        blockMessage: `This COD order has no ₹${MIN_COD_ADVANCE} advance.

Every COD order must carry the ₹${MIN_COD_ADVANCE} COD advance, paid online, before it can go to a courier. Add it with "Partial COD" in the row menu and send the payment link — or convert the order to prepaid.`,
    };
    // PARTIAL COD: the advance decides, not the confirmation call.
    //
    // A customer who has actually paid money online has proven both their
    // intent and a working payment instrument — which is precisely what the
    // COD confirmation call exists to establish, and it proves it better.
    // Making the desk also ring them is duplicated work that delays a parcel
    // the customer has already partly paid for.
    if (isPartialCod(order)) {
      if (canShipCod(order)) return { ok: true };
      return {
        ok: false,
        kind: 'cod_advance',
        label: 'Verify ₹' + codAdvance(order) + ' advance',
        hint: 'Partial COD — advance not received yet',
        blockMessage: `This is a partial-COD order with a ₹${codAdvance(order)} advance that has NOT been received.\n\nUse "Verify Advance" to check with Cashfree, or send the payment link and record it. The courier will collect the remaining ₹${Math.max(0, Number(order?.payment?.total || 0) - codAdvance(order))} on delivery.`,
      };
    }
    // Full COD still needs the call — nothing has been paid to prove intent.
    if (!isCODConfirmed(order)) return {
      ok: false,
      kind: 'cod',
      label: 'Confirm COD first',
      hint: 'Call & confirm to unlock courier',
      blockMessage: 'This COD order has not been call-verified.\n\nCall the customer and mark the COD confirmation (Call COD), then assign the courier.',
    };
    return { ok: true };
  }
  // Prepaid / online payment
  if (isPaidVerified(order)) {
    // Paid — but does the order still total what was actually received? Editing the
    // products recalculates the total, and it can drift away from the money taken.
    // Shipping on a drifted total means the invoice and the books disagree with the bank.
    const amt = amountIntegrity(order);
    if (!amt.ok) return { ok: false, kind: 'amount', label: amt.label, hint: amt.hint, blockMessage: amt.blockMessage };
    if (needsGatewayProof(order)) return {
      ok: false,
      kind: 'payment_proof',
      label: 'No gateway proof',
      hint: 'Website order marked PAID without a gateway payment id — run Sync Payments',
      blockMessage: 'This website order says PAID, but it carries no payment id from Cashfree or Razorpay.\n\nRun "Sync Payments" so the ERP checks the gateway itself, or verify it manually with the bank reference. Until then it cannot go to a courier.',
    };
    return { ok: true };
  }
  const vs = paymentVerification(order);
  const nice = vs === 'FAILED' ? 'Payment FAILED'
    : vs === 'ABANDONED' ? 'Payment was abandoned'
      : 'Payment not verified yet';
  return {
    ok: false,
    kind: 'payment',
    label: 'Verify payment first',
    hint: `${nice} — Sync Payments or verify manually`,
    blockMessage: `${nice}.\n\nA prepaid order can only ship once the payment is actually received and verified.\n\nRun "Sync Payments" (checks Cashfree automatically), or verify it manually from the row menu, then assign the courier.`,
  };
};
